import { type ReactNode, type SyntheticEvent, useId, useState } from 'react';
import { type AuthTokensResponse, type Locale } from '@aps/contracts';
import { Button, IconLogo } from '@aps/ui';
import { ApiError, api } from '../lib/api.js';
import { messages } from '../i18n/messages.js';

interface LoginScreenProps {
  locale: Locale;
  onSignedIn: (tokens: AuthTokensResponse) => void;
}

/**
 * Xodim kirishi.
 *
 * Ochiq ro'yxatdan o'tish YO'Q — login va parolni Owner beradi
 * (docs/06-OCHIQ-SAVOLLAR.md → 10-savol). Owner uchun qo'shimcha TOTP
 * maydoni bor; server uni talab qilsa xato matni shuni aytadi.
 */
export function LoginScreen({ locale, onSignedIn }: LoginScreenProps): ReactNode {
  const t = messages(locale);
  const loginId = useId();
  const passwordId = useId();
  const totpId = useId();

  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // `FormEvent` @types/react 19 da deprecated (u aslida DOM da yo'q).
  // `onSubmit` uzatadigan haqiqiy tip — `SyntheticEvent`.
  const submit = (event: SyntheticEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (busy) return;

    setBusy(true);
    setError(null);

    // TOTP bo'sh bo'lsa maydon UMUMAN yuborilmaydi: schema `.strict()` va
    // bo'sh string regexdan o'tmaydi — 400 qaytarardi.
    const trimmedTotp = totp.trim();

    api
      .staffLogin({
        staffLogin: login.trim(),
        password,
        ...(trimmedTotp === '' ? {} : { totp: trimmedTotp }),
      })
      .then((tokens) => {
        onSignedIn(tokens);
      })
      .catch((cause: unknown) => {
        setError(cause instanceof ApiError ? cause.text(locale) : t.errorTitle);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <main className="adm-login">
      <form className="adm-login__card" onSubmit={submit}>
        <div className="adm-brand">
          <span className="adm-brand__mark">
            <IconLogo size={32} />
          </span>
          <div className="adm-brand__text">
            <span className="adm-brand__title">{t.loginTitle}</span>
            <span className="adm-brand__subtitle">{t.appSubtitle}</span>
          </div>
        </div>

        <p className="aps-field__hint">{t.loginHint}</p>

        <div className="aps-field">
          <label className="aps-field__label" htmlFor={loginId}>
            {t.loginField}
          </label>
          <input
            id={loginId}
            className="aps-field__input"
            name="staffLogin"
            autoComplete="username"
            required
            value={login}
            onChange={(event) => {
              setLogin(event.target.value);
            }}
          />
        </div>

        <div className="aps-field">
          <label className="aps-field__label" htmlFor={passwordId}>
            {t.passwordField}
          </label>
          <input
            id={passwordId}
            className="aps-field__input"
            name="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
            }}
          />
        </div>

        <div className="aps-field">
          <label className="aps-field__label" htmlFor={totpId}>
            {t.totpField}
          </label>
          <input
            id={totpId}
            className="aps-field__input"
            name="totp"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={totp}
            onChange={(event) => {
              setTotp(event.target.value);
            }}
          />
          <span className="aps-field__hint">{t.totpHint}</span>
        </div>

        {error !== null && (
          <p className="aps-error" role="alert">
            {error}
          </p>
        )}

        <Button type="submit" block disabled={busy}>
          {busy ? t.signingIn : t.signIn}
        </Button>
      </form>
    </main>
  );
}
